import { forwardRef, InputHTMLAttributes, useState } from 'react';
import { motion } from 'framer-motion';
import { useUIV2Safe } from '@/contexts/UIV2Context';
import { cn } from '@/lib/utils';

export interface GlowInputProps extends InputHTMLAttributes<HTMLInputElement> {
  glowColor?: 'primary' | 'accent' | 'ring';
}

/**
 * GlowInput - Input with focus glow effect
 * V2 styled input component
 */
export const GlowInput = forwardRef<HTMLInputElement, GlowInputProps>(
  function GlowInput({ className, glowColor = 'primary', ...props }, ref) {
    const { isLowFx, uiV2Enabled } = useUIV2Safe();
    const [isFocused, setIsFocused] = useState(false);
    
    const glowClasses = {
      primary: 'focus:shadow-[0_0_20px_hsl(var(--primary)/0.3)]',
      accent: 'focus:shadow-[0_0_20px_hsl(var(--accent)/0.3)]',
      ring: 'focus:shadow-[0_0_20px_hsl(var(--ring)/0.3)]',
    };
    
    const baseClasses = cn(
      'flex h-10 w-full rounded-lg border border-input bg-background px-3 py-2',
      'text-sm ring-offset-background',
      'file:border-0 file:bg-transparent file:text-sm file:font-medium',
      'placeholder:text-muted-foreground',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      'disabled:cursor-not-allowed disabled:opacity-50',
      'transition-all duration-200',
      // V2 effects
      uiV2Enabled && !isLowFx && glowClasses[glowColor],
      uiV2Enabled && !isLowFx && 'focus:border-primary/50',
      uiV2Enabled && 'bg-card/50 backdrop-blur-sm',
      className
    );
    
    if (uiV2Enabled && !isLowFx) {
      return (
        <div className="relative">
          <input
            ref={ref}
            className={baseClasses}
            onFocus={(e) => {
              setIsFocused(true);
              props.onFocus?.(e);
            }}
            onBlur={(e) => {
              setIsFocused(false);
              props.onBlur?.(e);
            }}
            {...props}
          />
          {/* Animated glow ring */}
          <motion.div
            className="absolute inset-0 rounded-lg pointer-events-none"
            style={{
              boxShadow: `0 0 20px hsl(var(--${glowColor}) / 0.25)`,
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: isFocused ? 1 : 0 }}
            transition={{ duration: 0.2 }}
          />
        </div>
      );
    }
    
    return (
      <input
        ref={ref}
        className={baseClasses}
        {...props}
      />
    );
  }
);
